/**
 * Layers and the comment list.
 *
 * One reviewer, one layer, one colour, independently toggleable, plus the consolidated
 * view document control compiles the commented copy from. Carried-forward items are
 * labelled with the revision they were raised on and dispositioned in place — the gate
 * that blocks close-out is the same gate, surfaced where the work happens.
 */
import { useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, Loader2 } from 'lucide-react';
import api from '@/lib/api';

const DISPOSITIONS = [
  ['ACCEPTED', 'Accepted'],
  ['ACCEPTED_WITH_MODIFICATION', 'Accepted with modification'],
  ['REJECTED', 'Rejected (reason required)'],
  ['NOT_APPLICABLE', 'Not applicable (reason required)'],
  ['WITHDRAWN', 'Withdrawn by originator'],
  ['CARRIED_FORWARD', 'Carry forward to the next revision'],
];

export default function EdmMarkupPanel({ data, visibleLayers, setVisibleLayers, selectedId,
  onSelect, onChanged, gate }) {
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({});
  const layers = data?.layers || [];
  const markups = data?.markups || [];

  const toggle = (id) => {
    setVisibleLayers((prev) => {
      if (prev === null) return new Set(layers.filter((l) => l.layerId !== id).map((l) => l.layerId));
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const disposition = async (commentId) => {
    setBusy(commentId);
    try {
      await api.post(`/edm/comments/${commentId}/disposition`, {
        disposition: form.disposition,
        justification: form.justification,
        response: form.response,
      });
      setOpen(null); setForm({});
      onChanged();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setForm((f) => ({ ...f, error: typeof d === 'string' ? d : (d?.why || d?.error || 'Refused') }));
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-3" data-testid="edm-markup-panel">
      <div className="rounded-xl border p-3" style={{ borderColor: 'var(--border-color)' }}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>
            Reviewer layers
          </div>
          <button onClick={() => setVisibleLayers(null)} data-testid="edm-layer-consolidated"
            className="text-[11px] px-2 py-0.5 rounded border border-white/10 hover:bg-white/5">
            Consolidated
          </button>
        </div>
        {layers.length === 0 && (
          <div className="text-xs" style={{ color: 'var(--text-muted)' }} data-testid="edm-no-layers">
            No mark-ups on this revision yet. Pick a tool and draw on the sheet, or select
            elements in the model and attach a comment.
          </div>
        )}
        {layers.map((l) => {
          const on = visibleLayers === null || visibleLayers.has(l.layerId);
          return (
            <label key={l.layerId} className="flex items-center gap-2 py-1 cursor-pointer"
              data-testid={`edm-layer-${l.layerId}`}>
              <input type="checkbox" checked={on} onChange={() => toggle(l.layerId)}
                data-testid={`edm-layer-toggle-${l.layerId}`} />
              <span className="w-3 h-3 rounded-sm" style={{ background: l.colour }} />
              <span className="text-xs flex-1 truncate">{l.reviewer}</span>
              <span className="text-[11px] tabular-nums" style={{ color: 'var(--text-muted)' }}>
                {l.markupCount}{l.carriedCount ? ` · ${l.carriedCount} carried` : ''}
              </span>
            </label>
          );
        })}
        {data?.carriedStyle && (
          <div className="text-[11px] mt-2 pt-2 border-t" style={{
            borderColor: 'var(--border-color)', color: 'var(--text-muted)' }}
            data-testid="edm-carried-legend">
            Dashed and faded = {data.carriedStyle.why}
          </div>
        )}
      </div>

      {gate && !gate.ok && (
        <div className="rounded-xl border px-3 py-2 text-[12px]"
          style={{ borderColor: 'rgba(242,107,31,0.45)', background: 'rgba(242,107,31,0.07)' }}
          data-testid="edm-closeout-gate">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#F26B1F' }} />
            <div>{gate.blockReason}</div>
          </div>
        </div>
      )}
      {gate && gate.ok && (
        <div className="rounded-xl border px-3 py-2 text-[12px] flex items-center gap-2"
          style={{ borderColor: 'rgba(34,197,94,0.35)' }} data-testid="edm-closeout-clear">
          <CheckCircle2 className="w-4 h-4" style={{ color: '#22C55E' }} />
          Every comment on this revision has a disposition. Close-out is not blocked.
        </div>
      )}

      <div className="space-y-2" data-testid="edm-markup-list">
        {markups.map((m) => (
          <div key={m.comment_id}
            data-testid={`edm-markup-row-${m.comment_id}`}
            className={`rounded-lg border p-2.5 cursor-pointer transition-colors ${
              selectedId === m.comment_id ? 'bg-white/5' : ''}`}
            style={{ borderColor: 'var(--border-color)', borderLeft: `3px solid ${m.colour}` }}
            onClick={() => onSelect(m.comment_id)}>
            <div className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--text-muted)' }}>
              <span className="tabular-nums">#{m.comment_no}</span>
              <span className="truncate">{m.reviewer}</span>
              <span className="px-1.5 rounded bg-white/5 uppercase">{m.dimension}</span>
              <span className="px-1.5 rounded bg-white/5">{m.severity}</span>
              {m.markupVersion > 1 && (
                <span className="px-1.5 rounded bg-white/5" data-testid={`edm-markup-version-${m.comment_id}`}>
                  v{m.markupVersion}
                </span>
              )}
              {m.dimension === '2d' && <span>sheet {m.page}</span>}
            </div>
            {m.render?.label && (
              <div className="text-[11px] mt-1 italic" style={{ color: '#E2A93B' }}
                data-testid={`edm-markup-carried-${m.comment_id}`}>
                {m.render.label}
              </div>
            )}
            <div className="text-xs mt-1">{m.body || <em style={{ color: 'var(--text-muted)' }}>
              mark-up with no words</em>}</div>
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[11px]" style={{ color: m.status === 'OPEN' ? '#E2A93B' : '#22C55E' }}>
                {m.status === 'OPEN' ? 'Open — awaiting a disposition'
                  : `${m.status} · ${m.dispositionLabel || m.disposition}`}
              </span>
              {m.status === 'OPEN' && (
                <button data-testid={`edm-disposition-open-${m.comment_id}`}
                  onClick={(e) => { e.stopPropagation(); setOpen(open === m.comment_id ? null : m.comment_id); setForm({}); }}
                  className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded border border-white/10 hover:bg-white/5">
                  Disposition <ChevronDown className="w-3 h-3" />
                </button>
              )}
            </div>
            {open === m.comment_id && (
              <div className="mt-2 space-y-1.5" onClick={(e) => e.stopPropagation()}
                data-testid={`edm-disposition-form-${m.comment_id}`}>
                <select value={form.disposition || ''} data-testid={`edm-disposition-select-${m.comment_id}`}
                  onChange={(e) => setForm((f) => ({ ...f, disposition: e.target.value }))}
                  className="w-full text-xs rounded px-2 py-1.5 bg-transparent border"
                  style={{ borderColor: 'var(--border-color)' }}>
                  <option value="">Choose a disposition…</option>
                  {DISPOSITIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <textarea placeholder="Response to the reviewer (optional)" rows={2}
                  data-testid={`edm-disposition-response-${m.comment_id}`}
                  value={form.response || ''}
                  onChange={(e) => setForm((f) => ({ ...f, response: e.target.value }))}
                  className="w-full text-xs rounded px-2 py-1.5 bg-transparent border"
                  style={{ borderColor: 'var(--border-color)' }} />
                <textarea placeholder="Reason (required for Rejected and Not applicable)" rows={2}
                  data-testid={`edm-disposition-justification-${m.comment_id}`}
                  value={form.justification || ''}
                  onChange={(e) => setForm((f) => ({ ...f, justification: e.target.value }))}
                  className="w-full text-xs rounded px-2 py-1.5 bg-transparent border"
                  style={{ borderColor: 'var(--border-color)' }} />
                {form.error && <div className="text-[11px] text-red-400">{form.error}</div>}
                <button disabled={!form.disposition || busy === m.comment_id}
                  data-testid={`edm-disposition-save-${m.comment_id}`}
                  onClick={() => disposition(m.comment_id)}
                  className="text-xs px-2.5 py-1 rounded font-medium disabled:opacity-40"
                  style={{ background: '#E8622C', color: '#fff' }}>
                  {busy === m.comment_id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Record it'}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
